import React, { memo, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledTooltip,
} from 'reactstrap';
import makeAnimated from 'react-select/animated';
import AsyncSelect from 'react-select/async';
import Select, { components } from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import debounce from 'debounce-promise';

import {
  BAR_ENGINE_UTILIZATION,
  BAR_FUEL_UTILIZATION,
  COMMAND,
  GAUGE,
  GAUGE_RANGE,
  IMAGE,
  LOCATION,
  MAINTENANCE,
  MAP,
  TEXT_ENGINE,
  TEXT_FUEL,
  TEXT_STATIC,
  TEXT_TAGS,
} from '../../Constants/WidgetTypes';
import IconsContext from '../../Context/IconsContext';
import GatewayReminderInstanceService from '../../Services/GatewayReminderInstanceService';
import { BASIC_SELECT_STYLES } from '../../Styles/SelectStyles';

import ModalLayout from '../ModalLayout';
import {
  AddDataRow,
  BarUtilizationTimeAggregation,
  CommandsCard,
  ConfigureAxisMaxMin,
  DisplayTextAsGridToggle,
  SignalsCard,
  TagsCard,
  TextStaticConfig,
  TimeSinceLastUpdatedToggle,
} from './WidgetModalDisplayComponents';
import ConfigureGaugeWidget from './ConfigureGaugeWidget';
import ConfigureImageWidget from './ConfigureImageWidget';

export default memo(CreateWidgetModalDisplay);

const animatedComponents = makeAnimated();
function CreateWidgetModalDisplay(props) {
  const {
    activeImageCoordinate,
    asset,
    clearImage,
    configError,
    createRequestPending,
    createWidget,
    datapointsMap,
    displayTextAsGrid,
    error,
    handleAddImageOverlaySignal,
    handleAddRange,
    handleAddStaticTextLink,
    handleAddWidgetCommand,
    handleAddWidgetSignal,
    handleAggregationSelect,
    handleColorInput,
    handleColorSelected,
    handleCoordinateClick,
    handleDatapointSelect,
    handleDisplayTextAsGridClick,
    handleImageClick,
    handleImageSelect,
    handleMinMaxValueUpdate,
    handleNameAndYScaleUpdate,
    handleOptionDisabled,
    handleRangeValueUpdate,
    handleRemoveImageOverlaySignal,
    handleRemoveRange,
    handleRemoveStaticTextLink,
    handleRemoveWidgetSignal,
    handleSignalSelect,
    handleStaticTextInput,
    handleStaticTextLinkInput,
    handleTagSelect,
    handleTimeSinceLastUpdatedClick,
    handleWidgetTypeSelect,
    helpText,
    image,
    imageCoordinatesMap,
    isOpen,
    maintenanceSelectRef,
    selectedAggregation,
    selectedType,
    showTimeSinceLastUpdated,
    tagOptions,
    tagsLoaded,
    tagsLoading,
    timeAggregationOptions,
    toggleImageOverlayModal,
    toggleModal,
    updateState,
    widgetName,
    widgetOptions,
    widgetSignals,
    widgetType,
    yMax,
    yMin,
  } = props;
  const { widgetIcon } = useContext(IconsContext);

  const hideSignals = useMemo(
      () =>
        new Set([
          BAR_ENGINE_UTILIZATION,
          BAR_FUEL_UTILIZATION,
          IMAGE,
          LOCATION,
          MAP,
          TEXT_ENGINE,
          TEXT_FUEL,
        ]).has(widgetType),
      [widgetType]
    ),
    reminderInstanceService = useMemo(
      () => new GatewayReminderInstanceService(),
      []
    );

  const templateHasGps = useMemo(
    () =>
      props.asset?.gtemplate?.datapoints?.some?.(
        (c) => c?.type?.toLowerCase?.() === 'gps'
      ),
    [props.asset?.gtemplate?.datapoints]
  );

  const isWidgetTypeOptionDisabled = useCallback(
    (type) => {
      switch (type?.value) {
        case LOCATION:
        case MAP:
          return !templateHasGps;
        default:
          return false;
      }
    },
    [templateHasGps]
  );

  const [optionsLoading, setOptionsLoading] = useState(false),
    loadInstanceOptions = useCallback(
      async (reminder_name) => {
        try {
          setOptionsLoading(true);
          const params = {
            gateway_id: asset?._id,
            limit: 100,
            page: 1,
            reminder_name,
          };
          const { data } =
            await reminderInstanceService.getPaginatedGatewayReminderInstances(
              params
            );
          setOptionsLoading(false);
          return data.map((instance) => {
            return {
              label: instance.GtemplateReminder?.name,
              value: instance.GtemplateReminder?._id,
            };
          });
        } catch (error) {
          setOptionsLoading(false);
          return [];
        }
      },
      [asset?._id, reminderInstanceService]
    );

  return (
    <ModalLayout
      centered
      isOpen={isOpen}
      scrollable
      size="lg"
      toggle={toggleModal}
    >
      <ModalHeader className="pb-0 border-bottom-0" toggle={toggleModal}>
        <img
          src={widgetIcon}
          alt="widget icon"
          className="img-fluid mx-auto"
          style={{ height: '4rem' }}
        />
        <p className="mb-0 modal-title-text text-center">New Smart Tile</p>
      </ModalHeader>
      <ModalBody className="three-quarter-text p-3 px-md-4 container-fluid d-flex flex-column">
        <div className="row">
          <div className="col-12 form-group">
            <label htmlFor="widgetName">Smart Tile Name</label>
            <input
              name="widgetName"
              type="text"
              className="form-control"
              value={widgetName}
              onChange={handleNameAndYScaleUpdate}
            />
          </div>
        </div>
        <div className="row">
          <div className="form-group col-12 col-md-5">
            <label htmlFor="widgetType">Smart Tile Type</label>
            <Select
              classNamePrefix="select"
              components={{ Option }}
              isOptionDisabled={isWidgetTypeOptionDisabled}
              isSearchable
              name="widgetType"
              options={widgetOptions}
              onChange={handleWidgetTypeSelect}
              value={selectedType}
            />
          </div>
        </div>
        <div className="row" style={{ height: '2.5rem' }}>
          <p className="mb-0 col-12 text-muted helper-text">{helpText}</p>
        </div>
        <ConfigureAxisMaxMin
          handleNameAndYScaleUpdate={handleNameAndYScaleUpdate}
          widgetType={widgetType}
          yMax={yMax}
          yMin={yMin}
        />
        <AddDataRow
          handleAddWidgetSignal={handleAddWidgetSignal}
          selectedType={selectedType}
          widgetSignals={widgetSignals}
          widgetType={widgetType}
        />
        <BarUtilizationTimeAggregation
          handleAggregationSelect={handleAggregationSelect}
          selectedAggregation={selectedAggregation}
          timeAggregationOptions={timeAggregationOptions}
          widgetType={widgetType}
        />
        <TimeSinceLastUpdatedToggle
          handleTimeSinceLastUpdatedClick={handleTimeSinceLastUpdatedClick}
          showTimeSinceLastUpdated={showTimeSinceLastUpdated}
          widgetType={widgetType}
        />
        <DisplayTextAsGridToggle
          displayTextAsGrid={displayTextAsGrid}
          handleDisplayTextAsGridClick={handleDisplayTextAsGridClick}
          widgetType={widgetType}
        />
        {widgetType === IMAGE ? (
          <ConfigureImageWidget
            activeImageCoordinate={activeImageCoordinate}
            clearImage={clearImage}
            datapointsMap={datapointsMap}
            gtemplateID={asset?.gtemplateID}
            handleAddImageOverlaySignal={handleAddImageOverlaySignal}
            handleCoordinateClick={handleCoordinateClick}
            handleImageClick={handleImageClick}
            handleImageSelect={handleImageSelect}
            handleRemoveImageOverlaySignal={handleRemoveImageOverlaySignal}
            image={image}
            imageCoordinatesMap={imageCoordinatesMap}
            orgID={asset?.orgID}
            toggleImageOverlayModal={toggleImageOverlayModal}
            widgetType={widgetType}
          />
        ) : null}
        <div
          className={`row position-relative ${
            widgetType === IMAGE ? '' : 'flex-fill'
          }`}
        >
          {widgetType ? (
            <div
              className={
                hideSignals
                  ? 'd-none'
                  : 'overflow-auto col-12 h-100 signals-inner-container'
              }
            >
              {widgetSignals.map((s, i) => {
                switch (widgetType) {
                  case COMMAND:
                    return (
                      <CommandsCard
                        asset={asset}
                        command={s}
                        handleAddCommand={handleAddWidgetCommand}
                        handleRemoveCommand={handleRemoveWidgetSignal}
                        index={i}
                        key={s?.command?.value ?? i}
                        selectedCommands={widgetSignals}
                      />
                    );
                  case GAUGE:
                  case GAUGE_RANGE:
                    return (
                      <ConfigureGaugeWidget
                        datapointsMap={datapointsMap}
                        gtemplateID={asset?.gtemplateID}
                        handleAddRange={handleAddRange}
                        handleColorInput={handleColorInput}
                        handleColorSelected={handleColorSelected}
                        handleDatapointSelect={handleDatapointSelect}
                        handleMinMaxValueUpdate={handleMinMaxValueUpdate}
                        handleOptionDisabled={handleOptionDisabled}
                        handleRangeValueUpdate={handleRangeValueUpdate}
                        handleRemoveRange={handleRemoveRange}
                        handleRemoveWidgetSignal={handleRemoveWidgetSignal}
                        handleSignalSelect={handleSignalSelect}
                        index={i}
                        key={i}
                        orgID={asset?.orgID}
                        row={s}
                        updateState={updateState}
                        widgetType={widgetType}
                      />
                    );
                  case MAINTENANCE:
                    return (
                      <AsyncSelect
                        cacheOptions
                        components={animatedComponents}
                        defaultOptions
                        isClearable
                        isLoading={optionsLoading}
                        isMulti
                        isSearchable
                        key={`maintenance-reminder-select-${i}`}
                        loadOptions={debounce(loadInstanceOptions, 1000)}
                        placeholder="Search by Reminder Name"
                        ref={maintenanceSelectRef}
                        styles={BASIC_SELECT_STYLES}
                      />
                    );
                  case TEXT_STATIC:
                    return (
                      <TextStaticConfig
                        handleAddStaticTextLink={handleAddStaticTextLink}
                        handleRemoveStaticTextLink={handleRemoveStaticTextLink}
                        handleStaticTextInput={handleStaticTextInput}
                        handleStaticTextLinkInput={handleStaticTextLinkInput}
                        key={i}
                        widgetSignals={widgetSignals}
                      />
                    );
                  case TEXT_TAGS:
                    return (
                      <TagsCard
                        handleRemoveTag={handleRemoveWidgetSignal}
                        handleTagSelect={handleTagSelect}
                        index={i}
                        key={i}
                        row={s}
                        selectedTags={widgetSignals}
                        tagOptions={tagOptions}
                        tagsLoaded={tagsLoaded}
                        tagsLoading={tagsLoading}
                      />
                    );
                  default:
                    return (
                      <SignalsCard
                        datapointsMap={datapointsMap}
                        gtemplateID={asset?.gtemplateID}
                        handleDatapointSelect={handleDatapointSelect}
                        handleOptionDisabled={handleOptionDisabled}
                        handleRemoveWidgetSignal={handleRemoveWidgetSignal}
                        handleSignalSelect={handleSignalSelect}
                        index={i}
                        key={i}
                        orgID={asset?.orgID}
                        row={s}
                        updateState={updateState}
                      />
                    );
                }
              })}
            </div>
          ) : null}
        </div>
        <div
          className={configError ? 'row mt-2 justify-content-end' : 'd-none'}
        >
          <p className="col-auto mb-0 text-danger">{configError}</p>
        </div>
        <div className={error ? 'row mt-2 justify-content-end' : 'd-none'}>
          <p className="col-auto mb-0 text-danger"> {error}</p>
        </div>
      </ModalBody>
      <ModalFooter className="justify-content-between border-0 px-md-4">
        <button
          className="btn btn-outline-secondary py-0 px-3"
          onClick={toggleModal}
        >
          Cancel
        </button>
        <button
          className="btn modal-button-submit py-0 px-3"
          disabled={configError}
          onClick={createWidget}
        >
          {createRequestPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            'Create'
          )}
        </button>
      </ModalFooter>
      <style jsx>{`
        .close-modal-x {
          right: 3px;
          top: 3px;
          font-size: 1rem;
          background-color: transparent;
          text-decoration: none;
          outline: none;
          cursor: pointer;
        }
        .btn {
          font-size: 0.75rem;
        }
        @media (min-width: 768px) {
          .signals-inner-container {
            position: absolute;
          }
        }
      `}</style>
      <style jsx global>{`
        .modal-title {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          justify-content: center;
          margin-right: -1.5rem;
        }
      `}</style>
    </ModalLayout>
  );
}

CreateWidgetModalDisplay.propTypes = {
  activeImageCoordinate: PropTypes.object,
  asset: PropTypes.object,
  clearImage: PropTypes.func,
  createWidget: PropTypes.func,
  datapointsMap: PropTypes.arrayOf(PropTypes.object),
  configError: PropTypes.string,
  createRequestPending: PropTypes.bool,
  displayTextAsGrid: PropTypes.bool,
  error: PropTypes.string,
  handleAddImageOverlaySignal: PropTypes.func,
  handleAddRange: PropTypes.func,
  handleAddStaticTextLink: PropTypes.func,
  handleAddWidgetCommand: PropTypes.func,
  handleAddWidgetSignal: PropTypes.func,
  handleAggregationSelect: PropTypes.func,
  handleColorInput: PropTypes.func,
  handleColorSelected: PropTypes.func,
  handleCoordinateClick: PropTypes.func,
  handleDatapointSelect: PropTypes.func,
  handleDisplayTextAsGridClick: PropTypes.func,
  handleImageClick: PropTypes.func,
  handleImageSelect: PropTypes.func,
  handleMinMaxValueUpdate: PropTypes.func,
  handleNameAndYScaleUpdate: PropTypes.func,
  handleOptionDisabled: PropTypes.func,
  handleRangeValueUpdate: PropTypes.func,
  handleRemoveImageOverlaySignal: PropTypes.func,
  handleRemoveRange: PropTypes.func,
  handleRemoveStaticTextLink: PropTypes.func,
  handleRemoveWidgetSignal: PropTypes.func,
  handleSignalSelect: PropTypes.func,
  handleStaticTextInput: PropTypes.func,
  handleStaticTextLinkInput: PropTypes.func,
  handleTagSelect: PropTypes.func,
  handleTimeSinceLastUpdatedClick: PropTypes.func,
  handleWidgetTypeSelect: PropTypes.func,
  helpText: PropTypes.string,
  image: PropTypes.string,
  imageCoordinatesMap: PropTypes.object,
  isOpen: PropTypes.bool,
  maintenanceSelectRef: PropTypes.object,
  maxValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedAggregation: PropTypes.string,
  selectedType: PropTypes.object,
  showTimeSinceLastUpdated: PropTypes.bool,
  tagOptions: PropTypes.arrayOf(PropTypes.object),
  tagsLoaded: PropTypes.bool,
  tagsLoading: PropTypes.bool,
  timeAggregationOptions: PropTypes.arrayOf(PropTypes.object),
  toggleImageOverlayModal: PropTypes.func,
  toggleModal: PropTypes.func,
  updateState: PropTypes.func,
  widgetName: PropTypes.string,
  widgetOptions: PropTypes.arrayOf(PropTypes.object),
  widgetSignals: PropTypes.arrayOf(PropTypes.object),
  widgetType: PropTypes.string,
  yMax: PropTypes.string,
  yMin: PropTypes.string,
};

CreateWidgetModalDisplay.defaultProps = {
  widgetSignals: [],
  selectedType: {},
};

function Option(props) {
  return props.isDisabled ? (
    <>
      <components.Option {...props} />
      <UncontrolledTooltip target={props.innerProps.id}>
        Asset Model does not have a GPS Signal Collection
      </UncontrolledTooltip>
    </>
  ) : (
    <components.Option {...props} />
  );
}

Option.propTypes = {
  innerProps: PropTypes.shape({ id: PropTypes.string }),
  isDisabled: PropTypes.bool,
};
